import React, { useState, useEffect, useCallback } from 'react';
import Button from '../Button/Button';
import { usePayments } from '../../hooks/usePayments';
import { useToast } from '../../contexts/ToastContext';
import type { AppointmentPaymentDetails } from '../../services/api';
import styles from './AppointmentPaymentDetail.module.css';

interface AppointmentPaymentDetailProps {
    isOpen: boolean;
    appointmentId: number;
    onClose: () => void;
}

const getStatusClass = (status: string): string => {
    const s = status.toLowerCase();
    if (s === 'paid' || s === 'captured' || s === 'completed') return styles.statusPaid;
    if (s === 'pending') return styles.statusPending;
    if (s === 'failed') return styles.statusFailed;
    if (s === 'refunded') return styles.statusRefunded;
    return styles.statusUnpaid;
};

const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const truncatePaymentIntentId = (id: string): string => {
    if (id.length <= 20) return id;
    return `${id.substring(0, 10)}...${id.substring(id.length - 6)}`;
};

const AppointmentPaymentDetail: React.FC<AppointmentPaymentDetailProps> = ({
    isOpen,
    appointmentId,
    onClose,
}) => {
    const [paymentDetails, setPaymentDetails] = useState<AppointmentPaymentDetails | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const { getAppointmentPaymentDetails } = usePayments();
    const { showToast } = useToast();

    const loadPaymentDetails = useCallback(async () => {
        if (!appointmentId) return;
        setIsLoading(true);
        try {
            const details = await getAppointmentPaymentDetails(appointmentId);
            setPaymentDetails(details);
        } catch {
            // error handled by hook
        } finally {
            setIsLoading(false);
        }
    }, [appointmentId, getAppointmentPaymentDetails]);

    useEffect(() => {
        if (isOpen && appointmentId) {
            loadPaymentDetails();
        }
        if (!isOpen) {
            setPaymentDetails(null);
        }
    }, [isOpen, appointmentId, loadPaymentDetails]);

    const handleCopyPaymentId = useCallback(async () => {
        if (!paymentDetails?.stripePaymentIntentId) return;
        try {
            await navigator.clipboard.writeText(paymentDetails.stripePaymentIntentId);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch {
            showToast('Failed to copy to clipboard', 'error');
        }
    }, [paymentDetails, showToast]);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    const stripeTestDashboardUrl = paymentDetails?.stripePaymentIntentId
        ? `https://dashboard.stripe.com/test/payments/${paymentDetails.stripePaymentIntentId}`
        : null;

    return (
        <div className={`${styles.modalBackground} ${isOpen ? styles.open : ''}`} onClick={handleOverlayClick}>
            <div className={styles.modalContainer}>
                <div className={styles.headerRow}>
                    <h2 className={styles.headerText}>Payment Details</h2>
                    <button className={styles.closeButton} onClick={onClose}>&times;</button>
                </div>

                {isLoading ? (
                    <div className={styles.loadingState}>
                        <span>Loading payment details...</span>
                    </div>
                ) : !paymentDetails ? (
                    <div className={styles.emptyState}>
                        <h4>No payment found</h4>
                        <p>No payment record exists for this appointment yet.</p>
                    </div>
                ) : (
                    <>
                        <div className={styles.amountSection}>
                            <span className={styles.amountHighlight}>
                                {paymentDetails.amount.toFixed(2)}
                            </span>
                            <span className={styles.currencyLabel}>
                                {(paymentDetails.currency || 'AED').toUpperCase()}
                            </span>
                        </div>

                        <div className={styles.statusSection}>
                            <span className={`${styles.statusBadge} ${getStatusClass(paymentDetails.status)}`}>
                                {paymentDetails.status}
                            </span>
                        </div>

                        <div className={styles.detailsGrid}>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Stripe Payment ID</span>
                                <span className={styles.detailValue}>
                                    {truncatePaymentIntentId(paymentDetails.stripePaymentIntentId)}
                                    <button className={styles.copyButton} onClick={handleCopyPaymentId}>
                                        {copySuccess ? 'Copied' : 'Copy'}
                                    </button>
                                </span>
                            </div>

                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Payment Method</span>
                                <span className={styles.detailValue}>
                                    {paymentDetails.paymentMethod}
                                    {paymentDetails.cardBrand && paymentDetails.cardLast4 && (
                                        <> ({paymentDetails.cardBrand} ...{paymentDetails.cardLast4})</>
                                    )}
                                </span>
                            </div>

                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Date & Time</span>
                                <span className={styles.detailValue}>{formatDateTime(paymentDetails.paidAt)}</span>
                            </div>

                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Platform Fee</span>
                                <span className={styles.detailValue}>
                                    {paymentDetails.platformFeeAmount.toFixed(2)} {(paymentDetails.currency || 'AED').toUpperCase()} ({paymentDetails.platformFeeRate}%)
                                </span>
                            </div>

                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Net Amount</span>
                                <span className={styles.detailValue}>
                                    {paymentDetails.netAmount.toFixed(2)} {(paymentDetails.currency || 'AED').toUpperCase()}
                                </span>
                            </div>
                        </div>

                        {paymentDetails.promoCodeApplied && (
                            <div className={styles.promoSection}>
                                <span className={styles.promoLabel}>Promo Code: {paymentDetails.promoCodeApplied}</span>
                                <span className={styles.promoValue}>
                                    -{(paymentDetails.discountAmount ?? 0).toFixed(2)} {(paymentDetails.currency || 'AED').toUpperCase()}
                                </span>
                            </div>
                        )}

                        {stripeTestDashboardUrl && (
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Stripe Dashboard</span>
                                <a
                                    href={stripeTestDashboardUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.stripeLink}
                                >
                                    View in Stripe (Test Mode)
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10.5 1.5L1.5 10.5M10.5 1.5H4.5M10.5 1.5V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </a>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                            <Button label="Close" onClick={onClose} variant="secondary" size="small" />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AppointmentPaymentDetail;

/** Inline expandable variant for use within table rows */
export interface PaymentDetailInlineProps {
    transaction: {
        id: number;
        stripePaymentIntentId?: string;
        amount: number;
        currency?: string;
        status: string;
        method: string;
        date: string;
        platformFeeAmount?: number;
        platformFeeRate?: number;
        promoCodeApplied?: string;
        discountAmount?: number;
        netAmount?: number;
        cardLast4?: string;
        cardBrand?: string;
    };
}

export const PaymentDetailInline: React.FC<PaymentDetailInlineProps> = ({ transaction }) => {
    const [copySuccess, setCopySuccess] = useState(false);

    const handleCopy = async () => {
        if (!transaction.stripePaymentIntentId) return;
        try {
            await navigator.clipboard.writeText(transaction.stripePaymentIntentId);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch { /* ignore */ }
    };

    const stripeTestDashboardUrl = transaction.stripePaymentIntentId
        ? `https://dashboard.stripe.com/test/payments/${transaction.stripePaymentIntentId}`
        : null;

    return (
        <div className={styles.expandableContainer}>
            {transaction.stripePaymentIntentId && (
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Stripe Payment ID</span>
                    <span className={styles.detailValue}>
                        {truncatePaymentIntentId(transaction.stripePaymentIntentId)}
                        <button className={styles.copyButton} onClick={handleCopy}>
                            {copySuccess ? 'Copied' : 'Copy'}
                        </button>
                    </span>
                </div>
            )}

            <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Payment Method</span>
                <span className={styles.detailValue}>
                    {transaction.method}
                    {transaction.cardBrand && transaction.cardLast4 && (
                        <> ({transaction.cardBrand} ...{transaction.cardLast4})</>
                    )}
                </span>
            </div>

            <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Date</span>
                <span className={styles.detailValue}>{formatDateTime(transaction.date)}</span>
            </div>

            {transaction.platformFeeAmount != null && (
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Platform Fee</span>
                    <span className={styles.detailValue}>
                        {transaction.platformFeeAmount.toFixed(2)} {(transaction.currency || 'AED').toUpperCase()}
                        {transaction.platformFeeRate != null && ` (${transaction.platformFeeRate}%)`}
                    </span>
                </div>
            )}

            {transaction.netAmount != null && (
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Net Amount</span>
                    <span className={styles.detailValue}>
                        {transaction.netAmount.toFixed(2)} {(transaction.currency || 'AED').toUpperCase()}
                    </span>
                </div>
            )}

            {transaction.promoCodeApplied && (
                <div className={styles.promoSection}>
                    <span className={styles.promoLabel}>Promo: {transaction.promoCodeApplied}</span>
                    <span className={styles.promoValue}>
                        -{(transaction.discountAmount ?? 0).toFixed(2)} {(transaction.currency || 'AED').toUpperCase()}
                    </span>
                </div>
            )}

            {stripeTestDashboardUrl && (
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Stripe Dashboard</span>
                    <a
                        href={stripeTestDashboardUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.stripeLink}
                    >
                        View in Stripe (Test)
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.5 1.5L1.5 10.5M10.5 1.5H4.5M10.5 1.5V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </a>
                </div>
            )}
        </div>
    );
};
